import React, { useState, useEffect, useCallback } from 'react';
import {
    FlaskConical,
    Search,
    User,
    Clock,
    Upload,
    CheckCircle,
    Send,
    RefreshCw,
    FileText,
    Building2,
    ChevronDown,
    Wifi,
    Eye
} from 'lucide-react';
import './LabPanel.css';

import { API_URL } from '../config/api';
const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

const LabPanel = ({ embedded = false }) => {
    const [activeTab, setActiveTab] = useState('in_lab');
    const [studies, setStudies] = useState([]);
    const [laboratories, setLaboratories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    
    // Date filters for history
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    // Detail Modal
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedStudyDetail, setSelectedStudyDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Upload Result Modal
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedForUpload, setSelectedForUpload] = useState(null);
    const [uploadData, setUploadData] = useState({
        interpretation: '',
        pdf: null
    });
    const [submitting, setSubmitting] = useState(false);

    const fetchStudies = useCallback(async (showLoading = true) => {
        try {
            if (showLoading) setLoading(true);
            setIsRefreshing(true);
            const token = localStorage.getItem('token');
            const status = activeTab === 'in_lab' ? 'en_laboratorio' : 'finalizado';
            
            let url = `${API_URL}/lab/studies?status=${status}`;
            if (activeTab === 'finalizado') {
                url += `&month=${selectedMonth}&year=${selectedYear}`;
            }
            
            const res = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setStudies(data);
                setLastUpdated(new Date());
            }
        } catch (error) {
            console.error('Error fetching studies:', error);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, [activeTab, selectedMonth, selectedYear]);

    useEffect(() => {
        fetchStudies();

        const interval = setInterval(() => {
            fetchStudies(false);
        }, AUTO_REFRESH_INTERVAL);

        return () => clearInterval(interval);
    }, [fetchStudies]);

    const handleViewDetails = async (studyId) => {
        try {
            setDetailLoading(true);
            setShowDetailModal(true);
            setSelectedStudyDetail(null); // Clear previous

            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/patient-studies/${studyId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                const data = await res.json();
                setSelectedStudyDetail(data);
            } else {
                alert('Error al cargar detalles del estudio');
                setShowDetailModal(false);
            }
        } catch (error) {
            console.error('Error fetching details:', error);
            alert('Error de conexión');
            setShowDetailModal(false);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleUploadResult = async (e) => {
        e.preventDefault();
        if (!selectedForUpload) return;

        if (!uploadData.pdf) {
            alert('El archivo PDF del resultado es obligatorio para poder finalizar el estudio.');
            return;
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('interpretation', uploadData.interpretation);
            if (uploadData.pdf) {
                formData.append('pdf', uploadData.pdf);
            }

            const res = await fetch(`${API_URL}/patient-studies/${selectedForUpload.id}/result`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (res.ok) {
                // Complete the study — Bug 12 fix: verify success
                const completeRes = await fetch(`${API_URL}/patient-studies/${selectedForUpload.id}/complete`, {
                    method: 'PATCH',
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!completeRes.ok) {
                    const completeErr = await completeRes.json().catch(() => ({}));
                    alert('Resultado subido, pero no se pudo finalizar el estudio: ' + (completeErr.message || 'Error desconocido. Intente finalizar manualmente.'));
                }

                setShowUploadModal(false);
                setSelectedForUpload(null);
                setUploadData({ interpretation: '', pdf: null });
                fetchStudies();
            } else {
                const error = await res.json();
                alert(error.message || 'Error al subir resultado');
            }
        } catch (error) {
            alert('Error de conexión');
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const filteredStudies = studies.filter(study =>
        `${study.patient_first_name} ${study.patient_last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        study.study_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className={`lab-panel ${embedded ? 'embedded' : ''}`}>
            {!embedded && (
                <div className="panel-header">
                    <div className="header-title">
                        <div className="header-icon">
                            <FlaskConical size={28} />
                        </div>
                        <div>
                            <h1>Panel de Laboratorio</h1>
                            <p>Gestión de resultados y estudios en proceso</p>
                        </div>
                    </div>
                    <div className="header-actions">
                        <div className="live-indicator">
                            <Wifi size={14} className={isRefreshing ? 'pulse' : ''} />
                            <span>En vivo</span>
                            {lastUpdated && (
                                <span className="last-updated">
                                    · Actualizado {lastUpdated.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            )}
                        </div>
                        <button className={`btn-refresh ${isRefreshing ? 'refreshing' : ''}`} onClick={() => fetchStudies()}>
                            <RefreshCw size={18} className={isRefreshing ? 'spin' : ''} />
                            Actualizar
                        </button>
                    </div>
                </div>
            )}

            <div className="panel-tabs">
                <button
                    className={`tab-btn ${activeTab === 'in_lab' ? 'active' : ''}`}
                    onClick={() => setActiveTab('in_lab')}
                >
                    <FlaskConical size={18} />
                    En Proceso
                </button>
                <button
                    className={`tab-btn ${activeTab === 'finalizado' ? 'active' : ''}`}
                    onClick={() => setActiveTab('finalizado')}
                >
                    <CheckCircle size={18} />
                    Historial
                </button>
            </div>

            <div className="panel-filters">
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="Buscar paciente o estudio..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <span className="count-badge">
                    {filteredStudies.length} estudios {activeTab === 'in_lab' ? 'en proceso' : 'finalizados'}
                </span>

                {activeTab === 'finalizado' && (
                    <div className="date-filters">
                        <select 
                            value={selectedMonth} 
                            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                            className="filter-select"
                        >
                            <option value="1">Enero</option>
                            <option value="2">Febrero</option>
                            <option value="3">Marzo</option>
                            <option value="4">Abril</option>
                            <option value="5">Mayo</option>
                            <option value="6">Junio</option>
                            <option value="7">Julio</option>
                            <option value="8">Agosto</option>
                            <option value="9">Septiembre</option>
                            <option value="10">Octubre</option>
                            <option value="11">Noviembre</option>
                            <option value="12">Diciembre</option>
                        </select>
                        <select 
                            value={selectedYear} 
                            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                            className="filter-select"
                        >
                            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            <div className="studies-list">
                {loading ? (
                    <div className="loading-state">Cargando estudios...</div>
                ) : filteredStudies.length === 0 ? (
                    <div className="empty-state">
                        <CheckCircle size={48} strokeWidth={1} />
                        <p>No hay estudios {activeTab === 'in_lab' ? 'en proceso de laboratorio' : 'en el historial (finalizados)'}</p>
                    </div>
                ) : (
                    <div className="table-container">
                        <table className="studies-table">
                            <thead>
                                <tr>
                                    <th>Paciente</th>
                                    <th>Estudio</th>
                                    <th>Categoría</th>
                                    <th>Muestra</th>
                                    <th>Laboratorio</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredStudies.map(study => (
                                    <tr key={study.id}>
                                        <td>
                                            <div className="patient-cell">
                                                <User size={16} />
                                                <span>{study.patient_first_name} {study.patient_last_name}</span>
                                            </div>
                                        </td>
                                        <td>{study.study_name}</td>
                                        <td>
                                            <span className="category-badge">{study.category}</span>
                                        </td>
                                        <td>
                                            <div className="sample-cell">
                                                <Clock size={14} />
                                                {formatDate(study.collected_at)}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="lab-cell">
                                                <Building2 size={14} />
                                                {study.laboratory_name || study.catalog_laboratory || '-'}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                <button
                                                    className="btn-action view"
                                                    onClick={() => handleViewDetails(study.id)}
                                                    title="Ver Detalle"
                                                >
                                                    <Eye size={18} />
                                                </button>
                                                {activeTab === 'in_lab' && (
                                                    <button
                                                        className="btn-action upload"
                                                        onClick={() => {
                                                            setSelectedForUpload(study);
                                                            setShowUploadModal(true);
                                                        }}
                                                        title="Subir Resultado"
                                                    >
                                                        <Upload size={18} />
                                                    </button>
                                                )}
                                                {activeTab === 'finalizado' && (
                                                    <button
                                                        className="btn-action upload"
                                                        onClick={() => {
                                                            setSelectedForUpload(study);
                                                            setShowUploadModal(true);
                                                        }}
                                                        title="Reemplazar Resultado"
                                                        style={{ opacity: 0.7 }}
                                                    >
                                                        <Upload size={18} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Send Modal Removed */}

            {/* Detail Modal */}
            {showDetailModal && (
                <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
                    <div className="modal-content detail-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Detalle del Estudio</h2>
                            <button className="close-btn" onClick={() => setShowDetailModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            {detailLoading || !selectedStudyDetail ? (
                                <div className="loading-state">Cargando información...</div>
                            ) : (
                                <div className="study-details">
                                    <div className="detail-section">
                                        <h3>Información General</h3>
                                        <div className="detail-grid">
                                            <div className="detail-item">
                                                <label>Paciente:</label>
                                                <span>{selectedStudyDetail.patient_first_name} {selectedStudyDetail.patient_last_name}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>Estudio:</label>
                                                <span>{selectedStudyDetail.study_name} ({selectedStudyDetail.study_code})</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>Categoría:</label>
                                                <span>{selectedStudyDetail.category}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>Laboratorio:</label>
                                                <span>{selectedStudyDetail.laboratory_name || selectedStudyDetail.catalog_laboratory || 'No asignado'}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>Fecha Solicitud:</label>
                                                <span>{formatDate(selectedStudyDetail.requested_at)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Consent section removed as requested */}

                                    <div className="detail-section">
                                        <h3>Resultados</h3>
                                        {selectedStudyDetail.result_id ? (
                                            <div className="result-info">
                                                {selectedStudyDetail.pdf_path && (
                                                    <a
                                                        href={selectedStudyDetail.pdf_path.startsWith('http') ? selectedStudyDetail.pdf_path : `${API_URL}/${selectedStudyDetail.pdf_path}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn-view-pdf"
                                                    >
                                                        <FileText size={16} /> Ver PDF de Resultado
                                                    </a>
                                                )}
                                                {selectedStudyDetail.interpretation && (
                                                    <div className="interpretation-box">
                                                        <label>Interpretación / Notas:</label>
                                                        <p>{selectedStudyDetail.interpretation}</p>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <p className="no-result">Aún no se han cargado resultados.</p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn-cancel" onClick={() => setShowDetailModal(false)}>
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Result Modal */}
            {showUploadModal && selectedForUpload && (
                <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
                    <div className="modal-content upload-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Subir Resultado</h2>
                            <button className="close-btn" onClick={() => setShowUploadModal(false)}>×</button>
                        </div>

                        <form onSubmit={handleUploadResult}>
                            <div className="modal-body">
                                <div className="study-summary">
                                    <p><strong>Paciente:</strong> {selectedForUpload.patient_first_name} {selectedForUpload.patient_last_name}</p>
                                    <p><strong>Estudio:</strong> {selectedForUpload.study_name}</p>
                                </div>

                                <div className="form-group">
                                    <label>Archivo PDF del resultado</label>
                                    <div className="file-upload">
                                        <input
                                            type="file"
                                            accept=".pdf"
                                            onChange={(e) => setUploadData(prev => ({ ...prev, pdf: e.target.files[0] }))}
                                            id="pdf-upload"
                                            required
                                        />
                                        <label htmlFor="pdf-upload" className="file-label">
                                            <Upload size={20} />
                                            {uploadData.pdf ? uploadData.pdf.name : 'Seleccionar archivo PDF'}
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Interpretación / Notas</label>
                                    <textarea
                                        value={uploadData.interpretation}
                                        onChange={(e) => setUploadData(prev => ({ ...prev, interpretation: e.target.value }))}
                                        placeholder="Escriba la interpretación del resultado..."
                                        rows={5}
                                    />
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn-cancel" onClick={() => setShowUploadModal(false)}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-submit success" disabled={submitting}>
                                    {submitting ? 'Guardando...' : 'Guardar y Finalizar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LabPanel;
